export type Props = { [key: string]: any };

class ReactProps {
  public static hasProp(props: Props, propName: string): boolean {
    return props.hasOwnProperty(propName);
  }

  public static getPropValue<T = any>(props: Props, propName: string, defaultValue?: T): T | undefined {
    return ReactProps.hasProp(props, propName) ? props[propName] : defaultValue;
  }

  public static getRequiredProp<T = any>(props: Props, propName: string, missingPropCallback?: () => void): T {
    if (!ReactProps.hasProp(props, propName)) {
      if (missingPropCallback) {
        missingPropCallback();
      }
      throw new Error(`Missing required prop: ${propName}`);
    }
    return props[propName];
  }

  public static doesPropMatchRegex(props: Props, propName: string, regex: RegExp): boolean {
    const prop = props[propName];
    return typeof prop === "string" && regex.test(prop);
  }

  public static isPropInstanceOf(props: Props, propName: string, instance: any): boolean {
    return props[propName] instanceof instance;
  }

  public static isPropOfType(props: Props, propName: string, type: string): boolean {
    return typeof props[propName] === type;
  }

  public static getPropsOfType(props: Props, type: string): Props {
    const result: Props = {};
    for (let key in props) {
      if (typeof props[key] === type) {
        result[key] = props[key];
      }
    }
    return result;
  }

  public static countProps(props: Props): number {
    return Object.keys(props).length;
  }

  public static checkPropCondition<T = any>(props: Props, propName: string, condition: (value: T) => boolean): boolean {
    return ReactProps.hasProp(props, propName) && condition(props[propName]);
  }

  public static getPropsByCondition<T = any>(props: Props, condition: (value: T) => boolean): Props {
    const result: Props = {};
    for (let key in props) {
      if (ReactProps.hasProp(props, key) && condition(props[key])) {
        result[key] = props[key];
      }
    }
    return result;
  }

  public static arePropsEqual(props: Props, propName1: string, propName2: string): boolean {
    return (
      ReactProps.hasProp(props, propName1) &&
      ReactProps.hasProp(props, propName2) &&
      props[propName1] === props[propName2]
    );
  }

  public static pickProps(props: Props, propNames: string[]): Props {
    const result: Props = {};
    for (let propName of propNames) {
      if (ReactProps.hasProp(props, propName)) {
        result[propName] = props[propName];
      }
    }
    return result;
  }

  public static mergeProps(...propsObjects: Props[]): Props {
    return Object.assign({}, ...propsObjects);
  }

  public static excludeProps(props: Props, propNames: string[]): Props {
    let result = { ...props };
    for (let propName of propNames) {
      delete result[propName];
    }
    return result;
  }

  public static renameProp(props: Props, oldName: string, newName: string): Props {
    let result = { ...props };
    if (result.hasOwnProperty(oldName)) {
      result[newName] = result[oldName];
      delete result[oldName];
    }
    return result;
  }

  public static transformProp<T = any, U = any>(props: Props, propName: string, transform: (value: T) => U): Props {
    let result = { ...props };
    if (result.hasOwnProperty(propName)) {
      result[propName] = transform(result[propName]);
    }
    return result;
  }

  public static arePropsPresent(props: Props, propNames: string[]): boolean {
    return propNames.every((propName) => ReactProps.hasProp(props, propName));
  }

  public static getPropTypes(props: Props): { [key: string]: string } {
    const propTypes: { [key: string]: string } = {};
    for (let propName in props) {
      propTypes[propName] = typeof props[propName];
    }
    return propTypes;
  }

  public static arePropsSameType(props: Props, propNames: string[]): boolean {
    if (propNames.length === 0) {
      return true;
    }

    const firstPropType = typeof props[propNames[0]];
    return propNames.every((propName) => typeof props[propName] === firstPropType);
  }

  public static isPropInNumericRange(props: Props, propName: string, min: number, max: number): boolean {
    const propValue = props[propName];
    return typeof propValue === "number" && propValue >= min && propValue <= max;
  }

  public static hasNullOrUndefined(props: Props): boolean {
    return Object.values(props).some((value) => value === null || value === undefined);
  }

  public static getNullOrUndefinedProps(props: Props): string[] {
    return Object.keys(props).filter((key) => props[key] === null || props[key] === undefined);
  }

  public static removeNullOrUndefined(props: Props): Props {
    const result: Props = { ...props };
    for (let key in result) {
      if (result[key] === null || result[key] === undefined) {
        delete result[key];
      }
    }
    return result;
  }
  public static isPropOneOf(props: Props, propName: string, values: any[]): boolean {
    return values.includes(props[propName]);
  }
  public static mapProps<T = any, U = any>(props: Props, mapper: (key: string, value: T) => U): { [key: string]: U } {
    const result: { [key: string]: U } = {};
    for (let key in props) {
      result[key] = mapper(key, props[key]);
    }
    return result;
  }

  public static arePropsBoolean(props: Props, propNames: string[]): boolean {
    return propNames.every((propName) => typeof props[propName] === "boolean");
  }

  public static areAllPropsBoolean(props: Props): boolean {
    return Object.values(props).every((value) => typeof value === "boolean");
  }

  public static filterProps<T = any>(props: Props, condition: (key: string, value: T) => boolean): Props {
    const result: Props = {};
    for (let key in props) {
      if (condition(key, props[key])) {
        result[key] = props[key];
      }
    }
    return result;
  }

  public static reduceProps<T = any, U = any>(
    props: Props,
    reducer: (accumulator: U, key: string, value: T) => U,
    initialValue: U,
  ): U {
    let result = initialValue;
    for (let key in props) {
      result = reducer(result, key, props[key]);
    }
    return result;
  }

  public static getProp(obj: Props, path: string, defaultValue?: any, separator = "."): any {
    const parts = path.split(separator);
    let current: any = obj;
    for (let i = 0; i < parts.length; i++) {
      if (!ReactProps.hasProp(current, parts[i])) {
        return defaultValue;
      }
      current = current[parts[i]];
    }
    return current;
  }

  public static setProp(obj: Props, path: string, value: any, separator = "."): void {
    const parts = path.split(separator);
    const last = parts.pop() as string;
    const target = ReactProps.getProp(obj, parts.join(separator), obj);
    if (target && typeof target === "object") {
      target[last] = value;
    }
  }

  public static flattenProps(props: Props, prefix = "", separator = "."): Props {
    return Object.keys(props).reduce((flatProps: Props, key: string) => {
      const propName = prefix ? `${prefix}${separator}${key}` : key;
      if (typeof props[key] === "object" && !Array.isArray(props[key]) && props[key] !== null) {
        Object.assign(flatProps, ReactProps.flattenProps(props[key], propName));
      } else {
        flatProps[propName] = props[key];
      }
      return flatProps;
    }, {});
  }

  public static isRequired(props: Props, requiredProps: string[]): boolean {
    return requiredProps.every((key) => ReactProps.hasProp(props, key));
  }

  public static omitProps(props: Props, propNames: string[]): Props {
    return Object.keys(props).reduce((remainingProps: Props, key: string) => {
      if (!propNames.includes(key)) {
        remainingProps[key] = props[key];
      }
      return remainingProps;
    }, {});
  }
}

export { ReactProps };
